import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Link, Redirect } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { usePortal } from '../hooks/usePortal';
import { getHomeUrl, isMobile } from '../helper';
import { LogoSpinnerFullCentered } from '../components/LogoSpinner';
import {
  FuturisticBackground,
  IsometricShapesDecoration,
} from '../components/FuturisticBackground';
import { HomeNavbar } from './Navbar/HomeNavbar';
import { Button } from '../components/common/Button';
import { useSelectDefaultWorkspace } from '../hooks/useSelectDefaultWorkspace';
import { CreateNewEventModal } from './Portal/CreateNewEventModal';
import { isDefaultBrandKit } from 'zync-common/helper/brandKit';
import { DesktopOnlyModal } from '../components/DesktopOnlyModal';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import { Notification, Notifications } from '../components/Notifications';
import { PrivacyPolicyConsentModal } from '../components/PrivacyPolicyConsentModal';
import { episodeTypes } from 'zync-common/types';
import { SoloEpisodeCard } from './Portal/SoloEpisodeCard';
import { checkIsFailedEpisode, checkIsSoloEpisode } from './Portal/shared';
import { soloSessionCreationOptions } from './Portal/NewSoloSessionSelectionModal';
import { logerror } from '../helper/contextualLogger';
import { soloSessionCreationKeys } from './Portal/constants';
import { LoadingSoloEpisodeCard } from './Portal/LoadingSoloEpisodeCard';
import { fetchFeedDataApi, markContentKitVisitedApi } from '../helper/api';
import { WorkspaceContext } from '../App';
import { getStaticAssetUrl } from '../helper/getStaticAssetUrl';
import { WelcomeDirect } from './SoloWelcome';
import { useSelector } from 'react-redux';
import { useWorkspacePlan } from '../hooks/useWorkspacePlan';
import { ModalBody, ModalTitle, ModalWindow } from '../components/Modal';
import { CheckInCircleIcon } from '../components/icons/CheckInCircleIcon';
import classNames from '../helper/classNames';
import { UpgradeButton } from './Navbar/NavbarComponents';
import { MobileSoloPortal } from './MobileSoloPortal/MobileSoloPortal';
import { FailedSessionCreationModal } from '../components/FailedSessionCreationModal';
import UserProgressBar from '../components/UserProgressBar';
import moment from 'moment';
import { SettingsIconV2 } from '../components/icons/SettingsIconV2';
import { ReelIconV2 } from '../components/icons/ReelIconV2';
import BackgroundWrapper from '../components/BackgroundWrapper';
import { UserIconV2 } from '../components/icons/UserIconV2';
import { PROCESSOR_BASE_URL } from '../helper/constants';
import aiSuggestIcon from '../assets/aiIcon.svg';
import newIcon from '../assets/newIcon.svg';

const fetchContentKitAsset = async (meetingSeriesId) => {
  const result = await fetch(
    `${PROCESSOR_BASE_URL}/asset?meetingSeriesId=${meetingSeriesId}&processorId=CONTENT_KIT_PAGE`
  );

  return result.json();
};

const AppSumoPlanInfoModal = () => {
  const { workspacePlanLabel } = useWorkspacePlan();

  const [isOpen, setIsOpen] = useState(true);

  return isOpen ? (
    <ModalWindow isOpen={isOpen} onClose={() => setIsOpen(false)} size="sm">
      <ModalTitle>
        <div className="flex gap-2 items-center w-full">
          <CheckInCircleIcon color="#00A3A5" /> Welcome!
        </div>
      </ModalTitle>
      <ModalBody>
        <div className="-mt-4 text-blue-dark mb-4">
          You are about to create a new space with your{' '}
          <strong>{workspacePlanLabel}</strong> plan.
        </div>
        <Button
          onClick={() => setIsOpen(false)}
          color={Button.colors.TEAL}
          padding={Button.padding.MEDIUM}
          size={Button.sizes.FULL}
        >
          <span className="text-sm font-medium">Continue</span>
        </Button>
      </ModalBody>
    </ModalWindow>
  ) : null;
};

const ClipsLeft = () => {
  const { workspace } = useContext(WorkspaceContext);
  const { isTrial } = useWorkspacePlan();
  const history = useHistory();

  const [isHovered, setIsHovered] = useState(false);

  const { tierLimits, unlimitedClips } = workspace || {};
  const { isActive: isUnlimitedClipsActive = false } = unlimitedClips || {};

  const userId = useSelector((st) => st.auth?.user?.userId);

  if (isTrial) {
    return null;
  }

  if (isUnlimitedClipsActive) {
    return null;
  }

  if (!tierLimits) {
    return null;
  }

  const { limitPerMonth, enforceLimit, clipsCurrentMonth } =
    tierLimits?.clips || {};

  if (!enforceLimit) {
    return null;
  }

  const clipsLeft = limitPerMonth - Math.min(clipsCurrentMonth, limitPerMonth);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative"
    >
      <div className="text-white font-bold font-roboto">
        {clipsLeft} OF {limitPerMonth} VIDEOS LEFT
      </div>

      {isHovered ? (
        <div className="absolute bg-white p-3 bottom-0 right-0 translate-y-full z-10 flex items-center shadow-xl rounded-md border border-1 border-gray/50 gap-3">
          <p className="w-[364px] font-sm text-blue-dark font-medium">
            Upgrade for Unlimited Clips
          </p>
          <Button
            color={Button.colors.ORANGE}
            padding={Button.padding.SEMI_SMALL}
            onClick={() => {
              mixpanel.track('Portal: Clicked Upgrade', {
                distinct_id: userId,
              });
              history.push(
                `/workspace/billing?workspaceId=${workspace.workspaceId}`
              );
            }}
          >
            <div className="flex gap-1.5 items-center">
              <img
                width={24}
                height={24}
                src={getStaticAssetUrl('chargingIcon.png')}
                alt=""
              />
              <span className="text-sm">Upgrade</span>
            </div>
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const Container = React.memo(({ children }) => (
  <div className="flex flex-col gap-6 max-w-[1440px] px-[55px] mx-auto">
    {children}
  </div>
));

export const SoloPortal = () => {
  const {
    //authMode,
    authenticated,
    redirectToLanding,
    seriesList,
    seriesListLoading,
    brandKit,
    workspaceId,
    workspacePlan,
    currentWorkspaceName,
    isTrial,
    plan,
    soloSettings,
    workspacePlanValidUntil,
    currentSubscriptionRenewalTime,
    showPrivacyPolicyModal,
    setShowPrivacyPolicyModal,
    creatorUserId,
    fetchNewMeetings,
    requiresOnboarding,
    featureFlags,
    showFailedSessionCreationModal,
    setShowFailedSessionCreationModal,
    isWorkspaceOnboardingRequired,
  } = usePortal();

  const [contentKitAssetTimestamp, setContentKitAssetTimestamp] =
    useState(null);

  // Variable to store the URL to the earliest completed content kit
  const firstEverContentKit = useMemo(() => {
    if (!seriesList || seriesList.length === 0) return null;

    // Filter for solo episodes with completed content kit
    const completedEpisodes = seriesList.filter((series) => {
      const contentKitStatus = featureFlags.useContentKitWebhooks
        ? series?.contentKitStatuses?.CONTENT_KIT_PAGE?.status?.type
        : series?.contentKitStatus?.status;

      return (
        (contentKitStatus === 'COMPLETE' ||
          contentKitStatus === 'PROCESSING' ||
          contentKitStatus === 'WAITING_FOR_CALLBACK' ||
          contentKitStatus === 'WAITING FOR INPUT' ||
          contentKitStatus === 'INCOMPLETE') &&
        checkIsSoloEpisode(series)
      );
    });

    if (completedEpisodes.length === 0) return null;

    const sortedEpisodes = [...completedEpisodes].sort(
      (a, b) => new Date(a.creationTime) - new Date(b.creationTime)
    );

    // Get the earliest episode
    const earliestEpisode = sortedEpisodes[0];

    // Return the link to the content kit
    return earliestEpisode
      ? {
          status: featureFlags.useContentKitWebhooks
            ? earliestEpisode?.contentKitStatuses?.CONTENT_KIT_PAGE?.status
                ?.type
            : earliestEpisode?.contentKitStatus?.status,
          url: `/e/${earliestEpisode.meetingSeriesId}/details/publish?workspaceId=${workspaceId}`,
          id: earliestEpisode.meetingSeriesId,
          visited: earliestEpisode.contentKitVisited,
          visitedAt: earliestEpisode.contentKitVisitedAt,
        }
      : null;
  }, [seriesList, featureFlags, workspaceId]);

  // Check if ANY content kit has been visited
  const hasAnyContentKitBeenVisited = useMemo(() => {
    if (!seriesList || seriesList.length === 0) return false;

    return seriesList.some((series) => series.contentKitVisited);
  }, [seriesList]);

  const firstEverContentKitId = firstEverContentKit?.id;
  useEffect(() => {
    const fetchContentKitAssetTimestamp = async () => {
      if (firstEverContentKitId) {
        const { result } = await fetchContentKitAsset(firstEverContentKitId);
        setContentKitAssetTimestamp(result.status.timestamp);
      }
    };
    fetchContentKitAssetTimestamp();
  }, [firstEverContentKitId]);

  useEffect(() => {
    if (!firstEverContentKit?.id) return;

    // Check URL on initial load and navigation
    const handleUrlChange = () => {
      const currentUrl = window.location.pathname;
      if (currentUrl.includes(`/e/${firstEverContentKit.id}/details/publish`)) {
        // If we're on the content kit page, mark it as visited using the API
        markContentKitVisitedApi(firstEverContentKit.id);
      }
    };

    // Check on initial load
    handleUrlChange();

    // Set up event listeners for URL changes
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [firstEverContentKit]);

  const { workspaceType } = useWorkspacePlan();

  const [
    isNewManualScriptSoloSessionModalOpen,
    setIsNewManualScriptSoloSessionModalOpen,
  ] = useState(false);

  const [
    isNewBlogPostBasedSoloSessionModalOpen,
    setIsNewBlogPostBasedSoloSessionModalOpen,
  ] = useState(false);

  const [
    isNewIdeaBasedSoloSessionModalOpen,
    setIsNewIdeaBasedSoloSessionModalOpen,
  ] = useState(false);

  const [
    isNewProfileBasedSoloSessionModalOpen,
    setIsNewProfileBasedSoloSessionModalOpen,
  ] = useState(false);

  const [isNewZyncUpSessionModalOpen, setIsNewZyncUpSessionModalOpen] =
    useState(false);

  const [isNewRSessionModalOpen, setIsNewRSessionModalOpen] = useState(false);

  const [showDesktopOnlyModal, setShowDesktopOnlyModal] = useState(false);

  const [isFeedDataLoading, setIsFeedDataLoading] = useState(true);
  const [feedData, setFeedData] = useState(null);

  const getDesktopOnlyModalMessage = () => {
    switch (showDesktopOnlyModal) {
      case 'eventPage': {
        return 'The event page is only accessible from a desktop device.';
      }
      case 'eventCreation':
      default: {
        return 'New event creation can only happen from a desktop device.';
      }
    }
  };

  useSelectDefaultWorkspace(workspaceId);

  useEffect(() => {
    document.title = `${currentWorkspaceName || 'Studio'}`;
    return () => {
      document.title = `Studio`;
    };
  }, [currentWorkspaceName]);

  const userId = useSelector((st) => st.auth?.user?.userId);

  const { isAppSumo } = useWorkspacePlan();

  useEffect(() => {
    mixpanel.track('Portal: Entered Solo Portal', { distinct_id: userId });
  }, [userId]);

  useEffect(() => {
    if (!workspaceId) return;
    const fetchFeedData = async () => {
      const result = await fetchFeedDataApi(workspaceId);
      if (!result?.feedPage) {
        setIsFeedDataLoading(false);
        return;
      }

      setIsFeedDataLoading(false);
      setFeedData(result?.feedPage);
    };
    fetchFeedData();
  }, [workspaceId]);

  const filteredSeriesList = (seriesList || []).reduce(
    (filteredList, episode) => {
      switch (true) {
        case checkIsFailedEpisode(episode): {
          // do nothing
          break;
        }
        case checkIsSoloEpisode(episode): {
          filteredList.soloEpisodes.push(episode);
          break;
        }
        default: {
          break;
        }
      }

      return filteredList;
    },
    {
      soloEpisodes: [],
    }
  );

  const { soloEpisodes } = filteredSeriesList;

  const defaultSpeaker = soloSettings?.defaultSpeaker;
  const hasDefaultSpeaker = Boolean(defaultSpeaker);

  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const templateKey = urlSearchParams.get('templateKey');
  const invite = urlSearchParams.get('invite');

  if (isWorkspaceOnboardingRequired) {
    return (
      <Redirect
        to={`/solostudiowelcome?templateKey=${templateKey}${
          invite === 'true' ? '&invite=true' : ''
        }&workspaceId=${workspaceId}`}
      />
    );
  }

  if (requiresOnboarding) {
    return (
      <>
        {isAppSumo ? <AppSumoPlanInfoModal /> : null}
        <WelcomeDirect workspaceId={workspaceId} />
      </>
    );
  }

  if (workspaceType === 'podcast') {
    return <Redirect to={`/podcast?workspaceId=${workspaceId}`} />;
  }

  if (redirectToLanding) {
    return <Redirect to={getHomeUrl()} />;
  }

  if (isMobile) {
    return (
      <MobileSoloPortal
        seriesListLoading={seriesListLoading}
        brandKit={brandKit}
        soloSettings={soloSettings}
        currentWorkspaceName={currentWorkspaceName}
        workspaceId={workspaceId}
        soloEpisodes={soloEpisodes}
        plan={plan}
        fetchNewMeetings={fetchNewMeetings}
        workspacePlan={workspacePlan}
        creatorUserId={creatorUserId}
        featureFlags={featureFlags}
      />
    );
  }

  return (
    <>
      {showFailedSessionCreationModal && (
        <FailedSessionCreationModal
          onClose={() => {
            setShowFailedSessionCreationModal(false);
          }}
        />
      )}

      {showPrivacyPolicyModal && (
        <PrivacyPolicyConsentModal
          setShowPrivacyPolicyModal={setShowPrivacyPolicyModal}
        />
      )}
      {showDesktopOnlyModal && (
        <DesktopOnlyModal
          message={getDesktopOnlyModalMessage()}
          onClose={() => setShowDesktopOnlyModal(false)}
        />
      )}
      <FuturisticBackground
        showDecorations={false}
        showGradient={false}
        showIsometricLinesDecoration={false}
        topGradientOpacity={FuturisticBackground.topGradientOpacity.LOW}
      />
      <HomeNavbar
        isAuthenticated={authenticated}
        workspaceId={workspaceId}
        isTrial={isTrial}
        plan={plan}
        trialUntil={workspacePlanValidUntil}
        workspacePlanValidUntil={workspacePlanValidUntil}
        currentSubscriptionRenewalTime={currentSubscriptionRenewalTime}
        showCreateRSession={true}
        setIsNewRSessionModalOpen={setIsNewRSessionModalOpen}
      />
      {seriesListLoading && !brandKit?.primaryColor ? (
        <div className="h-[90vh] grid place-content-center">
          <LogoSpinnerFullCentered />
        </div>
      ) : (
        <div>
          <div className="relative">
            <Container>
              {!seriesListLoading && defaultSpeaker ? (
                <BackgroundWrapper
                  className="rounded-lg relative mt-0 max-w-[1225px] w-full mx-auto"
                  color={brandKit?.accentColor}
                  shadowOpacity={0.6}
                  contentClassName="h-full top-1/2 -translate-y-1/2 !absolute w-full"
                  backgroundColor={brandKit?.primaryColor}
                >
                  <div className="flex items-center w-full gap-4 absolute inset-0 font-roboto p-[5px]">
                    <p
                      style={{
                        color: brandKit?.accentContrast,
                      }}
                      className={classNames('absolute text-xxs right-2 top-2')}
                    >
                      {currentWorkspaceName}
                    </p>
                    <div
                      className="h-full aspect-square border-white border-2 rounded-[10px] overflow-hidden"
                      style={{
                        borderColor: brandKit?.accentColor,
                        backgroundColor: brandKit?.accentContrast,
                      }}
                    >
                      {defaultSpeaker.presenterPictureUrl ? (
                        <img
                          src={defaultSpeaker.presenterPictureUrl}
                          alt={defaultSpeaker.fullName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="p-1.5 flex items-center justify-center">
                          <UserIconV2
                            width="100%"
                            height="100%"
                            color={brandKit?.accentColor}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h3
                        className="text-[30px] font-normal"
                        style={{
                          color: brandKit?.primaryContrast,
                          lineHeight: 1,
                        }}
                      >
                        {defaultSpeaker.fullName}
                      </h3>
                      <p
                        className="text-[17px] whitespace-nowrap w-full font-normal"
                        style={{
                          color: brandKit?.primaryContrast,
                          lineHeight: 1,
                        }}
                      >
                        {defaultSpeaker.jobTitle}
                      </p>
                    </div>
                    <div className="absolute right-2 bottom-2 flex gap-3">
                      {!isFeedDataLoading &&
                        (feedData?.highlights || []).length > 0 && (
                          <Link
                            to={`/${workspaceId}/videofeed`}
                            onClick={() => {
                              mixpanel.track('Portal: Clicked Video Reel', {
                                distinct_id: userId,
                              });
                            }}
                          >
                            <ReelIconV2 width={32} height={32} />
                            <span className="sr-only">
                              Click to open video reel
                            </span>
                          </Link>
                        )}
                      <Link
                        to={`/workspace/workspace-settings?workspaceId=${workspaceId}`}
                      >
                        <span className="sr-only">
                          Click to open solo studio settings
                        </span>
                        <SettingsIconV2 width={32} height={32} />
                      </Link>
                    </div>
                  </div>
                </BackgroundWrapper>
              ) : null}

              <Notifications
                render={[
                  {
                    component: (
                      <Notification
                        key={1}
                        title={
                          <Link to="/workspace/settings">
                            SETUP YOUR BRAND KIT
                          </Link>
                        }
                        message={`You will have to setup your brand kit before running your first episode.${
                          isDefaultBrandKit(brandKit)
                            ? ' You are now using default Zync brand kit settings'
                            : ''
                        }`}
                      />
                    ),
                    when: isDefaultBrandKit(brandKit),
                  },
                ]}
              />
              {seriesListLoading ? (
                <LogoSpinnerFullCentered />
              ) : (
                <>
                  <UserProgressBar
                    hasCompletedRecording={!!firstEverContentKit}
                    hasViewedContentKit={hasAnyContentKitBeenVisited}
                    contentKitStatus={firstEverContentKit?.status}
                    contentKitUrl={firstEverContentKit?.url}
                    showProgressBar={
                      !firstEverContentKit ||
                      (contentKitAssetTimestamp &&
                        moment().isSameOrBefore(
                          moment(contentKitAssetTimestamp).add(1, 'days')
                        ))
                    }
                  />
                  <div className="max-w-[1225px] w-full flex flex-col gap-5 lg:items-start items-center self-center mt-[26px]">
                    <span className="text-blue-dark font-semibold text-xl">
                      Start Creating Your Next Video
                    </span>
                    <div className="flex lg:flex-row flex-col flex-wrap gap-2 w-full">
                      {soloSessionCreationOptions.map(
                        ({
                          aiSuggest,
                          itemKey,
                          icon,
                          header,
                          description,
                          linkText,
                          link,
                        }) => {
                          const isRedButton =
                            itemKey === soloSessionCreationKeys.zyncup;
                          return (
                            <div
                              key={`option-${itemKey}`}
                              style={{
                                flex: '1 1 0',
                                width: `calc(100% / ${soloSessionCreationOptions.length})`,
                                boxShadow:
                                  '0px 4px 4px -2px rgba(24, 39, 75, 0.08), 0px 2px 4px -2px rgba(24, 39, 75, 0.12)',
                              }}
                              className={classNames(
                                'will-change-transform min-w-full lg:min-w-[280px] group cursor-pointer relative h-[75px] rounded-[15px] flex flex-col justify-between border border-solid transition-all duration-200 ease-in-out hover:scale-[1.02] hover:shadow-md',
                                isRedButton
                                  ? 'bg-[#FFF7F4] border-[#EA3323] hover:bg-[#FFEFEA]'
                                  : 'bg-[#F5F2FA] border-[#8B33F7] hover:bg-[#EDE7F7]',
                                !hasDefaultSpeaker
                                  ? 'pointer-events-none opacity-50'
                                  : ''
                              )}
                              onClick={() => {
                                if (
                                  plan === 'free' &&
                                  itemKey !==
                                    soloSessionCreationKeys.manualScript &&
                                  itemKey !== soloSessionCreationKeys.zyncup
                                ) {
                                  return;
                                }
                                switch (itemKey) {
                                  case soloSessionCreationKeys.idea: {
                                    mixpanel.track(
                                      'Portal: New Session - From Idea Clicked',
                                      {
                                        distinct_id: userId,
                                      }
                                    );
                                    setIsNewIdeaBasedSoloSessionModalOpen(true);
                                    break;
                                  }
                                  case soloSessionCreationKeys.profile: {
                                    setIsNewProfileBasedSoloSessionModalOpen(
                                      true
                                    );
                                    break;
                                  }
                                  case soloSessionCreationKeys.blogPost: {
                                    mixpanel.track(
                                      'Portal: New Session - From Content Clicked',
                                      {
                                        distinct_id: userId,
                                      }
                                    );
                                    setIsNewBlogPostBasedSoloSessionModalOpen(
                                      true
                                    );
                                    break;
                                  }
                                  case soloSessionCreationKeys.manualScript: {
                                    mixpanel.track(
                                      'Portal: New Session - From Script Clicked',
                                      {
                                        distinct_id: userId,
                                      }
                                    );
                                    setIsNewManualScriptSoloSessionModalOpen(
                                      true
                                    );
                                    break;
                                  }
                                  case soloSessionCreationKeys.zyncup: {
                                    mixpanel.track(
                                      'Portal: New Session - Zync Up Clicked',
                                      { distinct_id: userId }
                                    );
                                    setIsNewZyncUpSessionModalOpen(true);
                                    break;
                                  }
                                  default: {
                                    logerror({
                                      message: `soloSessionCreationKey ${itemKey} not found. Did you add a new solo session creation key but did not update the onConfirm handler?`,
                                    });
                                  }
                                }
                              }}
                            >
                              <div className="flex flex-row p-2 gap-2 items-start h-full w-full">
                                <div className="h-[57px] w-[57px] flex items-center justify-center bg-white rounded-lg">
                                  {icon}
                                </div>
                                <div className="flex-1 flex flex-col h-full justify-center">
                                  <span className="text-[14px] text-[#1D1D1D] font-bold font-roboto">
                                    {header}
                                  </span>
                                  <span className="text-[12px] text-[#515151] font-normal font-roboto max-w-[127px] leading-4">
                                    {description}
                                  </span>
                                </div>
                              </div>

                              {plan === 'free' &&
                              itemKey !==
                                soloSessionCreationKeys.manualScript &&
                              itemKey !== soloSessionCreationKeys.zyncup ? (
                                <div className="hidden cursor-default group-hover:grid absolute inset-0 bg-white place-content-center rounded-lg z-10">
                                  <UpgradeButton />
                                </div>
                              ) : null}

                              {link && linkText && (
                                <div
                                  className={`absolute right-10 bottom-2 flex gap-1.5 items-center ${
                                    isRedButton
                                      ? 'text-[#E22515]'
                                      : 'text-[#8B33F7]'
                                  } text-[10px] self-start font-roboto font-normal underline cursor-pointer`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    window.open(
                                      link,
                                      '_blank',
                                      'noopener,noreferrer'
                                    );
                                  }}
                                >
                                  {linkText}
                                </div>
                              )}

                              <div
                                className={classNames(
                                  'flex gap-[5px] items-center text-black font-medium text-[9px] whitespace-nowrap absolute right-2 bottom-2',
                                  aiSuggest ? '' : 'hidden'
                                )}
                              >
                                <img src={aiSuggestIcon} alt="ai suggested" />
                              </div>
                              <div
                                className={classNames(
                                  'flex gap-[5px] items-center text-black font-medium text-[9px] whitespace-nowrap absolute right-2 bottom-2',
                                  itemKey === soloSessionCreationKeys.zyncup
                                    ? ''
                                    : 'hidden'
                                )}
                              >
                                <img src={newIcon} alt="new feature" />
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                  <div className="mb-4 flex flex-col items-center px-0 gap-0.5">
                    <div className="bg-purple rounded-[10px] px-[42px] py-5 flex justify-between items-center w-full max-w-[1225px]">
                      <h2 className="text-xl font-medium font-roboto text-white">
                        Video Recording Sessions
                      </h2>
                      <ClipsLeft />
                    </div>
                    <div className="flex flex-col w-full max-w-[1225px] gap-0.5">
                      {soloEpisodes.length ? (
                        soloEpisodes.map((series, i) =>
                          series.settings.episodeType ===
                          episodeTypes.placeholder ? (
                            <LoadingSoloEpisodeCard
                              key={series.meetingSeriesId}
                            />
                          ) : (
                            <SoloEpisodeCard
                              key={series.meetingSeriesId}
                              series={series}
                              setShowDesktopOnlyModal={setShowDesktopOnlyModal}
                              featureFlags={featureFlags}
                              isLatestEpisode={i === 0}
                              showRecordingTooltip={!firstEverContentKit}
                              hasCompletedRecording={!!firstEverContentKit}
                            />
                          )
                        )
                      ) : (
                        <div className="flex gap-4 justify-center items-center border border-t-0 border-gray/50 text-blue-gray text-center font-medium text-sm rounded-bl-lg rounded-br-lg p-4">
                          No recording sessions yet. Start your first video now!
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </Container>
          </div>
        </div>
      )}
      {isNewManualScriptSoloSessionModalOpen && (
        <CreateNewEventModal
          onClose={() => {
            mixpanel.track('New Session - Canceled Session Creation', {
              distinct_id: userId,
            });
            setIsNewManualScriptSoloSessionModalOpen(false);
            fetchNewMeetings();
          }}
          workspaceId={workspaceId}
          workspacePlan={workspacePlan}
          defaultHostId={creatorUserId}
          usePreview={false}
          episodeType={episodeTypes.soloScriptBased}
        />
      )}
      {isNewBlogPostBasedSoloSessionModalOpen && (
        <CreateNewEventModal
          onClose={() => {
            mixpanel.track('New Session - Canceled Session Creation', {
              distinct_id: userId,
            });
            setIsNewBlogPostBasedSoloSessionModalOpen(false);
            fetchNewMeetings();
          }}
          workspaceId={workspaceId}
          workspacePlan={workspacePlan}
          defaultHostId={creatorUserId}
          usePreview={false}
          episodeType={episodeTypes.soloBlogBased}
        />
      )}
      {isNewProfileBasedSoloSessionModalOpen && (
        <CreateNewEventModal
          onClose={() => {
            mixpanel.track('New Session - Canceled Session Creation', {
              distinct_id: userId,
            });
            setIsNewProfileBasedSoloSessionModalOpen(false);
            fetchNewMeetings();
          }}
          workspaceId={workspaceId}
          workspacePlan={workspacePlan}
          defaultHostId={creatorUserId}
          usePreview={false}
          episodeType={episodeTypes.soloProfileBased}
        />
      )}
      {isNewIdeaBasedSoloSessionModalOpen && (
        <CreateNewEventModal
          onClose={() => {
            mixpanel.track('New Session - Canceled Session Creation', {
              distinct_id: userId,
            });
            setIsNewIdeaBasedSoloSessionModalOpen(false);
            fetchNewMeetings();
          }}
          workspaceId={workspaceId}
          workspacePlan={workspacePlan}
          defaultHostId={creatorUserId}
          usePreview={false}
          episodeType={episodeTypes.soloIdeaBased}
        />
      )}
      {isNewZyncUpSessionModalOpen && (
        <CreateNewEventModal
          onClose={() => {
            mixpanel.track('New Session - Canceled Session Creation', {
              distinct_id: userId,
            });
            setIsNewZyncUpSessionModalOpen(false);
            fetchNewMeetings();
          }}
          workspaceId={workspaceId}
          workspacePlan={workspacePlan}
          defaultHostId={creatorUserId}
          usePreview={false}
          episodeType={episodeTypes.zyncup}
        />
      )}
      {isNewRSessionModalOpen && (
        <CreateNewEventModal
          onClose={() => {
            mixpanel.track('New Session - Canceled Session Creation', {
              distinct_id: userId,
            });
            setIsNewRSessionModalOpen(false);
            fetchNewMeetings();
          }}
          workspaceId={workspaceId}
          workspacePlan={workspacePlan}
          defaultHostId={creatorUserId}
          usePreview={false}
          episodeType={episodeTypes.responder}
        />
      )}
      <IsometricShapesDecoration className="absolute left-0 w-full object-cover" />
    </>
  );
};
